import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../base';
import { useLocation, useNavigate } from 'react-router-dom';
import { createCheckoutSession, createCustomerPortalSession, cancelSubscription } from '../services/stripeService';
import { toast } from 'react-toastify';
import pricingPlans from './pricingPlans';
import "./pricingSection.css";
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../base';
import featureFlags from '../config/featureFlags';
import PurchaseFeedback from '../components/PurchaseFeedback';
import { handleAnalyticsEvent } from '../helpers/analyticsHandler';
import { getStandardTrialFields } from '../helpers/standardTrial';

const EXTRA_GENERATION_CREDITS_PRICE_ID = 'price_1SAXsDLs3VmGBJkcN3YPlb1P';
const EXTRA_GENERATION_CREDITS_PRODUCT_ID = 'prod_T6lPqA2icpJwlv';
const EXTRA_GENERATION_CREDITS_PER_PURCHASE = 20;

function PricingSection({ handleGuestLogin, userRole, openAuthModal, requestEditorExitConfirmation }) {
    const { t, i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const [billingCycle, setBillingCycle] = useState('monthly');
    const [loadingPlan, setLoadingPlan] = useState(null);
    const currentUser = useAuth();
    const [userData, setUserData] = useState(null);
    const [userDataLoading, setUserDataLoading] = useState(true);
    const [isProcessingCancellation, setIsProcessingCancellation] = useState(false);
    const [isOpeningPortal, setIsOpeningPortal] = useState(false);
    const [isBuyingCredits, setIsBuyingCredits] = useState(false);

    const getLocalizedAppPath = (targetPath = '') => {
        const localePrefixes = ['/fr-CA', '/fr', '/en-US', '/en'];
        const localePrefix = localePrefixes.find((prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`)) || '/en';
        return `${localePrefix}/${targetPath}`.replace(/\/+/g, '/');
    };

    // Initialize stableUserRole from localStorage to avoid flicker on refresh
    const persistedRole = (typeof window !== 'undefined') ? localStorage.getItem('userRole') : null;
    const isPersistedAdmin = persistedRole === 'mainAdmin' || persistedRole === 'admin';
    const [stableUserRole, setStableUserRole] = useState(isPersistedAdmin ? persistedRole : undefined);

    // Add a ref to track if we've ever detected admin status
    const hasBeenAdminRef = useRef(isPersistedAdmin);

    // Add a confirmed admin role state to prevent losing admin status once detected
    const [confirmedAdminRole, setConfirmedAdminRole] = useState(null);

    useEffect(() => {
        let mounted = true;
        
        // Fast-track immediately if explicit admin role is present
        if (userRole === 'mainAdmin' || userRole === 'admin') {
            if (mounted) {
                setStableUserRole(userRole);
                setConfirmedAdminRole(userRole);
                hasBeenAdminRef.current = true;
                // Persist to localStorage
                if (typeof window !== 'undefined') {
                    localStorage.setItem('userRole', userRole);
                }
            }
            return () => { mounted = false; };
        }

        // If userRole is null (explicitly no role), only set to null if we've never been admin
        if (userRole === null) {
            if (mounted && !hasBeenAdminRef.current && !confirmedAdminRole) {
              setStableUserRole(null);
            }
            return () => { mounted = false; };
        }

        // For undefined (loading), wait a short time then set to persisted role or null
        const timeout = setTimeout(() => {
            if (!mounted) return;
            if (hasBeenAdminRef.current && confirmedAdminRole) {
              setStableUserRole(confirmedAdminRole);
            } else if (persistedRole === 'mainAdmin' || persistedRole === 'admin') {
              setStableUserRole(persistedRole);
              setConfirmedAdminRole(persistedRole);
              hasBeenAdminRef.current = true;
            } else {
              setStableUserRole(null);
            }
        }, 500);

        return () => { mounted = false; clearTimeout(timeout); };
    }, [userRole, persistedRole, confirmedAdminRole]);

    useEffect(() => {
      if ((userRole === 'mainAdmin' || userRole === 'admin') && !confirmedAdminRole) {
        setConfirmedAdminRole(userRole);
      }
    }, [userRole, confirmedAdminRole]);

    // Use confirmed admin role if available, otherwise use stableUserRole
    const effectiveUserRole = confirmedAdminRole || stableUserRole;
    const isAdminUser = effectiveUserRole === 'mainAdmin' || effectiveUserRole === 'admin';

    // Use isUserRoleLoaded to control loading UI: loaded when stableUserRole is not undefined
    const isUserRoleLoaded = typeof stableUserRole !== 'undefined';

    const isGuestUser = Boolean(currentUser?.email?.startsWith('guest_'));
    const isActiveStandardTrial = (data = userData) => {
        if (data?.standardTrialStatus !== 'active' || !data?.standardTrialEndsAt) {
            return false;
        }

        const rawEndsAt = data.standardTrialEndsAt;
        const endsAt = typeof rawEndsAt?.toDate === 'function' ? rawEndsAt.toDate() : new Date(rawEndsAt);
        return endsAt.getTime() > Date.now();
    };

    const hasStartedStandardTrial = (data = userData) => Boolean(
        data?.standardTrialStartedAt ||
        data?.standardTrialEndsAt ||
        (data?.standardTrialStatus && data.standardTrialStatus !== 'not_started')
    );

    const isEligibleForStandardTrial = () => {
        if (!currentUser?.uid || isGuestUser || isActiveStandardTrial() || hasStartedStandardTrial()) {
            return false;
        }

        const currentPlan = getCurrentPlanInfo();
        return !currentPlan?.isActive;
    };

    const promptForAuthenticatedPurchase = () => {
        const authMode = isGuestUser ? 'signup' : 'login';

        toast.error(t(
            isGuestUser ? 'pricing.signupRequired' : 'pricing.loginRequired',
            {
                defaultValue: isGuestUser
                    ? 'Create your account to purchase and keep your projects.'
                    : 'Please sign in or create an account before purchasing.'
            }
        ));

        if (typeof openAuthModal === 'function') {
            openAuthModal(authMode);
        }
    };

    // Fetch current user's plan data
    useEffect(() => {
        if (!currentUser?.uid) {
            setUserDataLoading(false);
            setUserData(null);
            return;
        }

        const unsubscribe = onSnapshot(doc(db, 'users', currentUser.uid), (doc) => {
            if (doc.exists()) {
                setUserData(doc.data());
            } else {
                setUserData(null);
            }
            setUserDataLoading(false);
        }, (error) => {
            console.error('Error fetching user data:', error);
            setUserDataLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser?.uid]);

    useEffect(() => {
        if (location.hash !== '#pricing') {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => {
            const pricingSection = document.getElementById('pricing');
            if (pricingSection) {
                pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
        }, 0);

        return () => window.clearTimeout(timeoutId);
    }, [location.hash]);

    // Translation mapping for plan features
    const translateFeature = (feature) => {
        const featureMap = {
            "1 project": "pricing.feature_1project_single",
            "App access for 30 days maximum": "pricing.feature_30days_access",
            "1 project maximum": "pricing.feature_1project",
            "7 days trial period": "pricing.feature_7days_trial", 
            "Basic AI assistance": "pricing.feature_basic_ai",
            "Community support": "pricing.feature_community_support",
            "Ads supported": "pricing.feature_ads_supported",
            "Up to 5 projects": "pricing.feature_5projects",
            "Up to 25 projects": "pricing.feature_25projects",
            "Full AI design assistance": "pricing.feature_full_ai",
            "Email support": "pricing.feature_email_support",
            "No ads": "pricing.feature_no_ads",
            "No watermark": "pricing.feature_no_watermark",
            "Unlimited projects": "pricing.feature_unlimited_projects",
            "Priority support": "pricing.feature_priority_support",
            "Export capabilities": "pricing.feature_export",
            "API access": "pricing.feature_api_access"
        };
        return t(featureMap[feature] || feature);
    };

    const getPlanAudienceLabel = (plan) => {
        switch (plan.name) {
            case 'my_project_plan':
                return t('pricing.planAudienceSingleProject', { defaultValue: 'Best for testing one home project' });
            case 'standard_plan':
                return t('pricing.planAudienceHomeowner', { defaultValue: 'Best for homeowners comparing multiple ideas' });
            case 'pro_plan':
                return t('pricing.planAudiencePro', { defaultValue: 'Best for professionals managing several projects' });
            default:
                return t('pricing.planAudienceDefault', { defaultValue: 'Choose the plan that matches your workflow' });
        }
    };

    const getPlanAccessLabel = (plan) => {
        if (plan.isOneTime) {
            return t('pricing.accessOneTime', { defaultValue: '30-day access' });
        }

        return billingCycle === 'annual'
            ? t('pricing.accessRenewsYearly', { defaultValue: 'Renews yearly' })
            : t('pricing.accessRenewsMonthly', { defaultValue: 'Renews monthly' });
    };

    const getPlanBillingSummary = (plan, currentPrice) => {
        if (plan.isOneTime) {
            return t('pricing.billingSummaryOneTime', { defaultValue: 'One payment, no renewal' });
        }

        if (billingCycle === 'annual') {
            const monthlyEquivalent = currentPrice / 12;
            return t('pricing.billingSummaryAnnual', {
                price: formatPrice(currentPrice),
                effectiveMonthly: formatPrice(monthlyEquivalent),
                defaultValue: '{{price}} billed yearly ({{effectiveMonthly}}/month effective)'
            });
        }

        return t('pricing.billingSummaryMonthly', {
            price: formatPrice(currentPrice),
            defaultValue: '{{price}} billed every month'
        });
    };

    // Helper function to determine user's current plan status
    const getCurrentPlanInfo = () => {
        if (!userData) return null;
        
        // Check for cancelled subscription with active My Project purchase
        const isSubscriptionCancelled = userData?.subscriptionStatus === 'cancel_at_period_end' || userData?.willCancelOn;
        const hasExtraProjects = userData?.extraProjectPurchased > 0;
        
        if (isSubscriptionCancelled && hasExtraProjects) {
            return {
                planName: 'My Project',
                planType: 'My Project',
                isActive: true,
                isSubscription: false,
                isCancelled: false
            };
        }
        
        return {
            planName: userData?.planName || userData?.currentPlan,
            planType: userData?.planType,
            isActive: userData?.subscriptionStatus === 'active' || userData?.isSubscription,
            isSubscription: userData?.isSubscription && userData?.billingCycle !== 'one-time',
            isCancelled: isSubscriptionCancelled
        };
    };

    // Helper function to determine button action and text
    const getPlanButtonInfo = (plan) => {
        const currentPlan = getCurrentPlanInfo();
        
        if (!currentUser || isGuestUser) {
            return {
                action: 'signup_purchase',
                text: plan.isOneTime ? t('pricing.purchase') : t('pricing.subscribe'),
                variant: 'primary',
                disabled: false
            };
        }

        if (plan.name === 'standard_plan' && isActiveStandardTrial()) {
            return {
                action: 'purchase',
                text: t('pricing.subscribeToKeepAccess', { defaultValue: 'Subscribe to keep access' }),
                variant: 'primary',
                disabled: false
            };
        }
        
        if (!currentPlan || !currentPlan.isActive) {
            return {
                action: 'purchase',
                text: plan.isOneTime ? t('pricing.purchase') : t('pricing.subscribe'),
                variant: 'primary',
                disabled: false
            };
        }
        
        // User has an active plan
        const currentPlanName = currentPlan.planName;
        const targetPlanName = plan.name;
        const currentBillingCycle = userData?.billingCycle || 'monthly';
        
        if (currentPlanName === targetPlanName) {
            if (plan.isOneTime) {
                return {
                    action: 'purchase',
                    text: t('pricing.purchase', { defaultValue: 'Purchase another project' }),
                    variant: 'primary',
                    disabled: false
                };
            }
            // Same plan - check if billing cycle is different
            if (currentBillingCycle !== billingCycle) {
                return {
                    action: 'change_billing',
                    text: billingCycle === 'annual' ? t('pricing.switchToAnnual') : t('pricing.switchToMonthly'),
                    variant: 'outline-primary',
                    disabled: false
                };
            }
            
            // Same plan and same billing cycle
            return {
                action: 'current',
                text: t('pricing.currentPlan'),
                variant: 'success',
                disabled: true
            };
        }
        
        // Determine if this is an upgrade or downgrade
        const planHierarchy = {
            'My Project': 1,
            'my_project_plan': 1,
            'standard_plan': 2,
            'pro_plan': 3,
            'Premium Facade': 4
        };
        
        const currentPlanLevel = planHierarchy[currentPlanName] || 0;
        const targetPlanLevel = planHierarchy[targetPlanName] || 0;
        
        if (targetPlanLevel > currentPlanLevel) {
            return {
                action: 'upgrade',
                text: t('pricing.upgrade'),
                variant: 'primary',
                disabled: false
            };
        } else if (targetPlanLevel < currentPlanLevel) {
            // Prevent downgrades - only upgrades are allowed
            return {
                action: 'downgrade_disabled',
                text: t('pricing.notAvailable'),
                variant: 'secondary',
                disabled: true
            };
        }
        
        return {
            action: 'purchase',
            text: plan.isOneTime ? t('pricing.purchase') : t('pricing.subscribe'),
            variant: 'primary',
            disabled: false
        };
    };

    const getPlanSecondaryButtonInfo = (plan) => {
        if (plan.name !== 'standard_plan') {
            return null;
        }

        if (!currentUser || isGuestUser) {
            return {
                action: 'signup_trial',
                text: t('pricing.startFreeTrial', { defaultValue: 'Start 7-day free trial' }),
                variant: 'secondary',
                disabled: false
            };
        }

        if (isEligibleForStandardTrial()) {
            return {
                action: 'start_trial',
                text: t('pricing.startFreeTrial', { defaultValue: 'Start 7-day free trial' }),
                variant: 'secondary',
                disabled: false
            };
        }

        return null;
    };

    const handleSubscribe = async (plan) => {
        // ✅ FIRST LINE SECURITY CHECK: Control access based on feature flags and user role
        if (!featureFlags.PAYMENTS_LIVE && !isAdminUser) {
            // If payments aren't live and user isn't admin, show payments not available
            toast.info(t('pricing.paymentsNotLive', { 
                defaultValue: 'Payments are not yet available. Please check back later!' 
            }));
            return;
        }
        
        if (!featureFlags.PAYMENTS_LIVE && isAdminUser) {
            // If payments aren't live but user is admin, allow testing with warning
            toast.info(t('pricing.adminTestMode', { 
                defaultValue: 'Testing payment flow in admin mode...' 
            }));
        }

        if (!currentUser || isGuestUser) {
            promptForAuthenticatedPurchase();
            return;
        }

        const executeSubscribe = async () => {
            setLoadingPlan(plan.name);
            
            try {
            const currentPlan = getCurrentPlanInfo();
            const buttonInfo = getPlanButtonInfo(plan);
            
            // Prevent downgrades explicitly
            if (buttonInfo.action === 'downgrade_disabled') {
                toast.error(t('pricing.downgradeNotAllowed'));
                return;
            }
            
            // Handle upgrade of existing subscription
            if (buttonInfo.action === 'upgrade' && currentPlan?.isSubscription && userData?.subscriptionId) {
                console.log(`Upgrading subscription from ${currentPlan.planName} to ${plan.name}`);
                
                // Determine the target price based on current billing cycle selection
                const targetPriceId = billingCycle === 'monthly' ? plan.stripePriceId : plan.stripePriceIdAnnual;
                const targetBillingCycle = billingCycle;
                
                console.log(`Target billing cycle: ${targetBillingCycle}, Price ID: ${targetPriceId}`);
                
                const response = await fetch('/.netlify/functions/upgrade-subscription', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId: currentUser.uid,
                        newPlanPriceId: targetPriceId,
                        newPlanName: plan.name,
                        newBillingCycle: targetBillingCycle
                    }),
                });

                const result = await response.json();
                
                if (response.ok) {
                    toast.success(t('pricing.upgradeSuccess'));
                    
                    // Force refresh user data to show immediate changes
                    console.log('Upgrade successful, user data should update automatically via Firebase listener');
                } else {
                    throw new Error(result.error || 'Upgrade failed');
                }
                return;
            }
            
            // Handle billing cycle change for same plan
            if (buttonInfo.action === 'change_billing' && currentPlan?.isSubscription && userData?.subscriptionId) {
                console.log(`Changing billing cycle for ${plan.name} from ${userData?.billingCycle || 'monthly'} to ${billingCycle}`);
                
                const targetPriceId = billingCycle === 'monthly' ? plan.stripePriceId : plan.stripePriceIdAnnual;
                
                const response = await fetch('/.netlify/functions/upgrade-subscription', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        userId: currentUser.uid,
                        newPlanPriceId: targetPriceId,
                        newPlanName: plan.name,
                        newBillingCycle: billingCycle
                    }),
                });

                const result = await response.json();
                
                if (response.ok) {
                    toast.success(t('pricing.billingCycleChanged'));
                    
                    console.log('Billing cycle change successful');
                } else {
                    throw new Error(result.error || 'Billing cycle change failed');
                }
                return;
            }
            
            // Handle new subscription or one-time purchase
            const priceId = plan.isOneTime ? plan.stripePriceId : 
                           (billingCycle === 'monthly' ? plan.stripePriceId : plan.stripePriceIdAnnual);
            
            if (!priceId) {
                throw new Error('Price ID not configured for this plan');
            }

            // Get current language for Stripe
            const currentLanguage = i18n.language || 'en';

            handleAnalyticsEvent('checkout_started', 'pricing', plan.name);
            await createCheckoutSession(
                priceId,
                currentUser.uid,
                plan.name,
                currentLanguage,
                plan.isOneTime ? 'one_time' : billingCycle,
                currentUser.email,
                currentUser.displayName || currentUser.email,
                {},
                currentUser.email
            );
            } catch (error) {
            console.error('Subscription error:', error);
            handleAnalyticsEvent('checkout_start_failed', 'pricing', plan.name);
            if (error.message === 'AUTH_REQUIRED' || error.code === 'AUTH_REQUIRED') {
                promptForAuthenticatedPurchase();
            } else if (error.message === 'EMAIL_MISMATCH' || error.code === 'EMAIL_MISMATCH') {
                toast.error(t('pricing.emailMismatch', {
                    defaultValue: 'Use the same email for your account and checkout.'
                }));
            } else if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
                toast.error(t('auth-emailNeedConfirm', {
                    defaultValue: 'Please confirm your email before signing in to your account.'
                }));
                if (typeof openAuthModal === 'function') {
                    openAuthModal('login');
                }
            } else if (error.message.includes('upgrade')) {
                toast.error(t('pricing.upgradeError'));
            } else {
                toast.error(t('Failed to start subscription process. Please try again.'));
            }
            } finally {
                setLoadingPlan(null);
            }
        };

        if (typeof requestEditorExitConfirmation === 'function') {
            requestEditorExitConfirmation({ onProceed: executeSubscribe });
            return;
        }

        executeSubscribe();
    };

    const handleStartStandardTrial = async () => {
        if (!currentUser?.uid || isGuestUser) {
            if (typeof openAuthModal === 'function') {
                openAuthModal('signup', { intent: 'trial', planName: 'standard_plan' });
            }
            return;
        }

        if (!isEligibleForStandardTrial()) {
            toast.info(t('pricing.trialUnavailable', {
                defaultValue: 'The 7-day Standard trial is only available once for new accounts without an active plan.'
            }));
            return;
        }

        if (!currentUser.emailVerified) {
            toast.info(t('pricing.trialVerifyEmail', {
                defaultValue: 'Confirm your email address before starting your 7-day trial.'
            }));
            return;
        }

        setLoadingPlan('standard_plan');

        try {
            await setDoc(doc(db, 'users', currentUser.uid), {
                ...getStandardTrialFields(),
                standardTrialSource: 'pricing_existing_account',
            }, { merge: true });
            handleAnalyticsEvent('trial_activated', 'trial', 'pricing_existing_account');

            toast.success(t('pricing.trialStarted', {
                defaultValue: 'Your 7-day Standard trial is now active. Upload your own house to get started.'
            }));
        } catch (error) {
            console.error('Error starting standard trial:', error);
            toast.error(t('pricing.trialStartError', {
                defaultValue: 'Unable to start your free trial right now. Please try again.'
            }));
        } finally {
            setLoadingPlan(null);
        }
    };

    const handleBuyAdditionalCredits = async () => {
        if (!currentUser?.uid || isGuestUser) {
            promptForAuthenticatedPurchase();
            return;
        }

        const executeBuyCredits = async () => {
            setIsBuyingCredits(true);

            try {
            const currentLanguage = i18n.language || 'en';
            await createCheckoutSession(
                EXTRA_GENERATION_CREDITS_PRICE_ID,
                currentUser.uid,
                'extra_generation_credits_pack',
                currentLanguage,
                'one-time',
                currentUser.email,
                userData?.userName || currentUser.displayName || '',
                {
                    purchaseType: 'extra_credits',
                    creditsAmount: EXTRA_GENERATION_CREDITS_PER_PURCHASE,
                    productId: EXTRA_GENERATION_CREDITS_PRODUCT_ID,
                },
                currentUser.email
            );
            } catch (error) {
            console.error('Error creating credits checkout:', error);
            if (error.code === 'EMAIL_VERIFICATION_REQUIRED') {
                toast.error(t('auth-emailNeedConfirm', {
                    defaultValue: 'Please confirm your email before signing in to your account.'
                }));
                if (typeof openAuthModal === 'function') {
                    openAuthModal('login');
                }
            } else {
                toast.error(t('pricing.buyCreditsError', { defaultValue: 'Unable to start checkout for additional credits.' }));
            }
            } finally {
                setIsBuyingCredits(false);
            }
        };

        if (typeof requestEditorExitConfirmation === 'function') {
            requestEditorExitConfirmation({ onProceed: executeBuyCredits });
            return;
        }

        executeBuyCredits();
    };

    const formatPrice = (price) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2
        }).format(price);
    };

    const getProjectCountLabel = (plan) => {
        if (plan?.name === 'my_project_plan') {
            return t('pricing.projectCount.myProject', { defaultValue: '1 project' });
        }
        if (plan?.name === 'standard_plan') {
            return t('pricing.projectCount.standard', { defaultValue: '5 projects' });
        }
        if (plan?.name === 'pro_plan') {
            return t('pricing.projectCount.pro', { defaultValue: '25 projects' });
        }
        if (plan?.projectLimit === 'unlimited') {
            return t('pricing.projectCount.unlimited', { defaultValue: 'Unlimited projects' });
        }

        return t('pricing.projectCount.generic', {
            count: plan?.projectLimit,
            defaultValue: '{{count}} projects'
        });
    };

    const getGenerationsPeriodLabel = (plan) => {
        if (plan?.name === 'my_project_plan') {
            return t('pricing.generationsNoRenew30Days', { defaultValue: 'Valid for 30 days (no renewal)' });
        }

        if (plan?.name === 'standard_plan') {
            return t('pricing.standardPlanGenerationsPeriod', { defaultValue: 'per month after subscription' });
        }

        return t('pricing.generationsRenewMonthly', { defaultValue: 'Renews every month' });
    };

    const getGenerationsCountLabel = (plan) => {
        if (plan?.name === 'standard_plan') {
            return `${plan?.includedGenerations ?? 0}`;
        }

        return `${plan?.includedGenerations ?? 0}`;
    };

    const getGenerationsSecondaryLabel = (plan) => {
        if (plan?.name === 'standard_plan') {
            return t('pricing.standardTrialGenerationsTrialNote', {
                defaultValue: '2 free-trial credits'
            });
        }

        return '';
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return null;
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString();
    };

    const isRecurringSubscription = Boolean(
        userData?.subscriptionId &&
        userData?.billingCycle !== 'one-time' &&
        userData?.billingCycle !== 'one_time'
    );

    const isScheduledForCancellation = userData?.subscriptionStatus === 'cancel_at_period_end' || Boolean(userData?.willCancelOn);

    const handleManageSubscriptionFromPricing = async () => {
        if (!userData?.stripeCustomerId) {
            toast.error(t('account.cancellationError', { defaultValue: 'Unable to manage subscription right now.' }));
            return;
        }

        setIsOpeningPortal(true);
        try {
            await createCustomerPortalSession(userData.stripeCustomerId);
        } catch (error) {
            console.error('Error opening billing portal from pricing section:', error);
            toast.error(t('account.cancellationError', { defaultValue: 'Unable to manage subscription right now.' }));
        } finally {
            setIsOpeningPortal(false);
        }
    };

    const handleCancelSubscriptionFromPricing = async () => {
        if (!userData?.subscriptionId || !currentUser?.uid) {
            toast.error(t('account.cancellationError', { defaultValue: 'Unable to cancel subscription right now.' }));
            return;
        }

        if (!window.confirm(t('account.cancelSubscriptionConfirm', { defaultValue: 'Are you sure you want to cancel your subscription? You will keep access until the end of the current billing period.' }))) {
            return;
        }

        setIsProcessingCancellation(true);
        try {
            const result = await cancelSubscription(userData.subscriptionId, currentUser.uid);

            if (result?.success) {
                const cancelDate = new Date(result.cancelDate).toLocaleDateString();
                toast.success(t('account.cancellationSuccess', {
                    date: cancelDate,
                    defaultValue: `Subscription will be canceled on ${cancelDate}.`
                }));
            } else {
                toast.error(t('account.cancellationError', { defaultValue: 'Unable to cancel subscription right now.' }));
            }
        } catch (error) {
            console.error('Error canceling subscription from pricing section:', error);
            toast.error(t('account.cancellationError', { defaultValue: 'Unable to cancel subscription right now.' }));
        } finally {
            setIsProcessingCancellation(false);
        }
    };

    const getDiscountPercentage = (monthly, annual) => {
        if (monthly === 0 || annual === 0) return 0;
        return Math.round(((monthly * 12 - annual) / (monthly * 12)) * 100);
    };

    // Update button text based on current user's plan and admin status
    const getButtonText = (plan) => {
        if (loadingPlan === plan.name) return 'Loading...';
        const buttonInfo = getPlanButtonInfo(plan);

        if (buttonInfo.action === 'start_trial' || buttonInfo.action === 'signup_trial') {
            return buttonInfo.text;
        }
        
        // If payments are live, show normal button text
        if (featureFlags.PAYMENTS_LIVE) {
            return buttonInfo.text;
        }
        
        // If payments aren't live but user is admin, show test purchase
        if (isAdminUser) return 'Test Purchase';
        
        // ✅ For regular users when payments aren't live, show "Coming Soon"
        return 'Coming Soon';
    };

    // Update button rendering to use the corrected logic
    const renderButton = (plan, index) => {
        const isLoading = !isUserRoleLoaded || userDataLoading;
        const buttonInfo = getPlanButtonInfo(plan);
        const isTrialAction = buttonInfo.action === 'start_trial' || buttonInfo.action === 'signup_trial';
        
        // ✅ SECURITY FIX: Button access based on feature flags and user role
        // If payments are live, everyone can use buttons
        // If payments are not live, only admins can use buttons (for testing)
        const isDisabled = isTrialAction ? false : (featureFlags.PAYMENTS_LIVE ? false : !isAdminUser);
        
        return (
            <button
                key={index}
                className={`plan-button ${plan.buttonClass} ${buttonInfo.variant === 'success' ? 'current-plan' : ''} ${isDisabled ? 'disabled' : ''}`}
                disabled={isLoading || isDisabled}
                onClick={() => {
                    handleAnalyticsEvent('plan_selected', 'pricing', plan.name);
                    if (buttonInfo.action === 'signup_trial') {
                        handleAnalyticsEvent('trial_cta_clicked', 'trial', 'pricing_primary');
                        if (typeof openAuthModal === 'function') {
                            openAuthModal('signup', { intent: 'trial', planName: 'standard_plan', source: 'pricing_primary' });
                        }
                        return;
                    }

                    if (buttonInfo.action === 'signup_purchase') {
                        handleAnalyticsEvent('purchase_signup_requested', 'pricing', plan.name);
                        if (typeof openAuthModal === 'function') {
                            openAuthModal('signup', { intent: 'purchase', planName: plan.name });
                        }
                        return;
                    }

                    if (buttonInfo.action === 'start_trial') {
                        handleAnalyticsEvent('trial_cta_clicked', 'trial', 'pricing_primary');
                        handleStartStandardTrial();
                        return;
                    }

                    // ✅ SECURITY CHECK: Control access based on feature flags and user role
                    if (!featureFlags.PAYMENTS_LIVE && !isAdminUser) {
                        toast.info(t('pricing.paymentsNotLive', { 
                            defaultValue: 'Payments are not yet available. Please check back later!' 
                        }));
                        return;
                    }
                    
                    if (!plan.stripePriceId) {
                        alert('Error: This plan has no price ID configured');
                        return;
                    }
                    
                    if (buttonInfo.action === 'current') {
                        return; // Do nothing for current plan
                    }
                    
                    if (buttonInfo.action === 'downgrade_disabled') {
                        toast.error(t('pricing.downgradeNotAllowed'));
                        return; // Prevent downgrades
                    }
                    
                    // Handle billing cycle changes and upgrades
                    handleSubscribe(plan);
                }}
            >
                {isLoading ? 'Loading...' : getButtonText(plan)}
            </button>
        );
    };

    const renderSecondaryButton = (plan, index) => {
        const isLoading = !isUserRoleLoaded || userDataLoading;
        const buttonInfo = getPlanSecondaryButtonInfo(plan);

        if (!buttonInfo) {
            return null;
        }

        return (
            <button
                key={`secondary-${index}`}
                className={`plan-button secondary ${buttonInfo.disabled ? 'disabled' : ''}`}
                disabled={isLoading || buttonInfo.disabled}
                onClick={() => {
                    if (buttonInfo.action === 'signup_trial') {
                        handleAnalyticsEvent('trial_cta_clicked', 'trial', 'pricing_secondary');
                        if (typeof openAuthModal === 'function') {
                            openAuthModal('signup', { intent: 'trial', planName: 'standard_plan', source: 'pricing_secondary' });
                        }
                        return;
                    }

                    if (buttonInfo.action === 'start_trial') {
                        handleAnalyticsEvent('trial_cta_clicked', 'trial', 'pricing_secondary');
                        handleStartStandardTrial();
                    }
                }}
            >
                {loadingPlan === 'standard_plan'
                    ? t('common.loading', { defaultValue: 'Loading...' })
                    : buttonInfo.text}
            </button>
        );
    };

    // Update plan data to have appropriate button text for admin users
    const plans = pricingPlans.map(plan => ({
        ...plan,
        buttonText: isAdminUser ? 'Test Purchase' : 'Coming Soon'
    }));

    // Debug: Log imported plans
    return (
        <div className="pricing-section" id="pricing">
            <div className="pricing-header">
                <h2 className="pricing-title">{t('pricing.headerTitle', { defaultValue: 'Choose Your Plan' })}</h2>
                <p className="pricing-subtitle">{t('pricing.headerSubtitle', { defaultValue: 'Start your facade transformation journey today' })}</p>

                <div className="pricing-guide">
                    <div className="pricing-guide-card">
                        <span className="pricing-guide-label">{t('pricing.guideStarterLabel', { defaultValue: 'Starter' })}</span>
                        <p>{t('pricing.guideStarterText', { defaultValue: 'My Project is a one-time option for a single facade project.' })}</p>
                    </div>
                    <div className="pricing-guide-card">
                        <span className="pricing-guide-label">{t('pricing.guideSubscriptionLabel', { defaultValue: 'Subscription' })}</span>
                        <p>{t('pricing.guideSubscriptionText', { defaultValue: 'Standard includes a 7-day free trial with no credit card. After the trial, Standard and Pro renew automatically only if you choose a paid plan.' })}</p>
                    </div>
                    <div className="pricing-guide-card">
                        <span className="pricing-guide-label">{t('pricing.guideCreditsLabel', { defaultValue: 'Extra credits' })}</span>
                        <p>{t('pricing.guideCreditsText', { defaultValue: 'You can buy additional AI generations later if you need more.' })}</p>
                    </div>
                </div>

                {currentUser && isRecurringSubscription && (
                    <div className="subscription-management-card">
                        <p className="subscription-management-title">
                            {t('account.subscriptionInfo', { defaultValue: 'Subscription management' })}
                        </p>

                        {isScheduledForCancellation && userData?.willCancelOn ? (
                            <p className="subscription-management-status">
                                {t('account.subscriptionWillCancel', {
                                    date: formatDate(userData.willCancelOn),
                                    defaultValue: `Your subscription is set to cancel on ${formatDate(userData.willCancelOn)}.`
                                })}
                            </p>
                        ) : (
                            <div className="subscription-management-actions">
                                <button
                                    type="button"
                                    className="plan-button secondary"
                                    onClick={handleManageSubscriptionFromPricing}
                                    disabled={isOpeningPortal || isProcessingCancellation}
                                >
                                    {isOpeningPortal
                                        ? t('common.loading', { defaultValue: 'Loading...' })
                                        : t('account.billingPortal', { defaultValue: 'Manage Billing' })}
                                </button>
                                <button
                                    type="button"
                                    className="plan-button secondary"
                                    onClick={handleCancelSubscriptionFromPricing}
                                    disabled={isProcessingCancellation || isOpeningPortal}
                                >
                                    {isProcessingCancellation
                                        ? t('common.loading', { defaultValue: 'Loading...' })
                                        : t('account.cancelSubscription', { defaultValue: 'Cancel Subscription' })}
                                </button>
                            </div>
                        )}
                    </div>
                )}

                {/* Billing cycle toggle - now enabled with annual pricing */}
                <div className="billing-cycle-switch">
                    <div className="billing-cycle-copy">
                        <p className="billing-cycle-title">{t('pricing.billingCycleTitle', { defaultValue: 'Choose how subscription plans renew' })}</p>
                        <p className="billing-cycle-subtitle">{t('pricing.billingCycleSubtitle', { defaultValue: 'My Project stays one-time. Standard starts with a free trial, and paid Standard or Pro can be monthly or annual.' })}</p>
                    </div>
                    <div className="toggle-container">
                        <button 
                            onClick={() => setBillingCycle('monthly')} 
                            className={`toggle-btn ${billingCycle === 'monthly' ? 'active' : ''}`}
                            disabled={!isUserRoleLoaded}
                        >
                            {t('Monthly')}
                        </button>
                        <button 
                            onClick={() => setBillingCycle('annual')} 
                            className={`toggle-btn ${billingCycle === 'annual' ? 'active' : ''}`}
                            disabled={!isUserRoleLoaded}
                        >
                            {t('Annual')}
                            <span className="discount-badge">{t('2 months free')}</span>
                        </button>
                    </div>
                </div>
            </div>

            <div className="pricing-plans">
                {plans.map(plan => {
                    const currentPrice = plan.isOneTime ? plan.monthlyPrice : 
                                       (billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice);
                    const discount = getDiscountPercentage(plan.monthlyPrice, plan.annualPrice);
                    const buttonInfo = getPlanButtonInfo(plan);
                    const isCurrentPlan = buttonInfo.action === 'current';
                    
                    const hasTopBadge = plan.popular || plan.isPromo;

                    return (
                        <div key={plan.id} className={`plan ${plan.popular ? 'popular' : ''} ${isCurrentPlan ? 'current-user-plan' : ''} ${plan.isPromo ? 'promo-plan' : ''} ${hasTopBadge ? 'has-top-badge' : ''}`}>
                            {plan.isPromo ? (
                                <div className="pricing-promo-badge">{t('pricing.limitedTimeOffer', { defaultValue: 'Limited Time Offer!' })}</div>
                            ) : (
                                plan.popular && <div className="pricing-popular-badge">{t('pricing.mostPopular', { defaultValue: 'Most Popular' })}</div>
                            )}
                            {isCurrentPlan && <div className="current-plan-badge">{t('pricing.currentPlan')}</div>}
                            
                            <div className="plan-header">
                                <div className="plan-eyebrow">{getPlanAudienceLabel(plan)}</div>
                                <h3 className="plan-name">{t(plan.name)}</h3>
                                <div className="plan-price">
                                    {plan.isPromo && plan.originalPrice && (
                                        <span className="original-price">{formatPrice(plan.originalPrice)}</span>
                                    )}
                                    <span className="price-amount">{formatPrice(currentPrice)}</span>
                                    {!plan.isOneTime && plan.monthlyPrice > 0 && (
                                        <span className="price-period">
                                            /{t(billingCycle === 'monthly' ? 'month' : 'year')}
                                        </span>
                                    )}
                                    {plan.isOneTime && (
                                        <span className="price-period">
                                            {t('one_time')}
                                        </span>
                                    )}
                                </div>
                                {plan.isPromo && plan.originalPrice && (
                                    <div className="savings promo-savings">
                                        {t('Save')} {Math.round(((plan.originalPrice - currentPrice) / plan.originalPrice) * 100)}%
                                    </div>
                                )}
                                {!plan.isOneTime && billingCycle === 'annual' && discount > 0 && (
                                    <div className="savings">
                                        {t('Save')} {discount}% {t('vs monthly')}
                                    </div>
                                )}
                                <div className="plan-projects">{getProjectCountLabel(plan)}</div>
                                <div className="plan-billing-summary">{getPlanBillingSummary(plan, currentPrice)}</div>
                            </div>

                            <div className="plan-highlights" aria-label={t('pricing.planHighlightsLabel', { defaultValue: 'Plan highlights' })}>
                                <div className="plan-highlight">
                                    <span className="plan-highlight-label">{t('pricing.highlightProjects', { defaultValue: 'Projects' })}</span>
                                    <strong>{getProjectCountLabel(plan)}</strong>
                                </div>
                                <div className="plan-highlight">
                                    <span className="plan-highlight-label">{t('pricing.highlightGenerations', { defaultValue: 'AI generations' })}</span>
                                    <strong>{plan?.includedGenerations ?? 0}</strong>
                                </div>
                                <div className="plan-highlight">
                                    <span className="plan-highlight-label">{t('pricing.highlightAccess', { defaultValue: 'Access' })}</span>
                                    <strong>{getPlanAccessLabel(plan)}</strong>
                                </div>
                            </div>

                            <div className="plan-generations-section">
                                <div className="plan-generations-title">
                                    {t('pricing.includedGenerationsTitle', { defaultValue: 'Included AI Generations' })}
                                </div>
                                <div className="plan-generations-count">
                                    {getGenerationsCountLabel(plan)}
                                </div>
                                <div className="plan-generations-period">
                                    {getGenerationsPeriodLabel(plan)}
                                </div>
                                {getGenerationsSecondaryLabel(plan) ? (
                                    <div className="plan-generations-secondary">
                                        {getGenerationsSecondaryLabel(plan)}
                                    </div>
                                ) : null}
                            </div>

                            <div className="plan-features">
                                <ul>
                                    {plan.features.map((feature, index) => (
                                        <li key={index}>
                                            <span className="feature-icon">✓</span>
                                            {translateFeature(feature)}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="plan-action">
                                {renderButton(plan)}
                                {renderSecondaryButton(plan)}
                            </div>
                        </div>
                    );
                })}
            </div>

            <PurchaseFeedback placement="pricing" />
            <div className="pricing-footer">
                <div className="credits-purchase-cta">
                    <p className="guest-cta-text">
                        {t('pricing.buyCreditsTitle', { defaultValue: 'Need more AI generations?' })}
                    </p>
                    <button
                        className="btn btn-outline-success mt-2"
                        onClick={handleBuyAdditionalCredits}
                        disabled={isBuyingCredits}
                    >
                        {isBuyingCredits
                            ? t('common.loading', { defaultValue: 'Loading...' })
                            : t('pricing.buyCreditsButton', { defaultValue: `Buy ${EXTRA_GENERATION_CREDITS_PER_PURCHASE} additional credits - $10` })}
                    </button>
                    <p className="credits-purchase-subtext">
                        {t('pricing.buyCreditsSubtext', { defaultValue: 'Credits are added to your account balance immediately after payment confirmation.' })}
                    </p>
                </div>

                {!currentUser || isGuestUser ? (
                    <div className="guest-mode-cta">
                        <p className="guest-cta-text">
                            {t('pricing.trialCtaText', { defaultValue: 'Use Guest Mode to explore the default project, then create an account to start your 7-day free trial on your own house.' })}
                        </p>
                        <button 
                            className="btn btn-primary mt-4"
                            onClick={(e) => {
                                e.preventDefault();
                                handleAnalyticsEvent('trial_cta_clicked', 'trial', 'pricing_footer');
                                if (typeof openAuthModal === 'function') {
                                    openAuthModal('signup', { intent: 'trial', planName: 'standard_plan', source: 'pricing_footer' });
                                } else {
                                    navigate(getLocalizedAppPath(''));
                                }
                            }}
                        >
                            {t('pricing.startFreeTrial', { defaultValue: 'Start free trial' })}
                        </button>
                        {handleGuestLogin ? (
                            <button
                                className="btn btn-link mt-2"
                                onClick={(e) => {
                                    e.preventDefault();
                                    handleGuestLogin({ allowAdminTest: userRole === 'mainAdmin' || userRole === 'admin' });
                                    setTimeout(() => {
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }, 100);
                                }}
                            >
                                {t('Try Guest Mode - No Registration Required')}
                            </button>
                        ) : null}
                    </div>
                ) : null}
                <div className="pricing-faq-link">
                    <a 
                        href="#faq"
                        onClick={(e) => {
                            e.preventDefault();
                            const faqSection = document.getElementById('faq');
                            if (faqSection) {
                                faqSection.scrollIntoView({ behavior: 'smooth' });
                            } else {
                                // If we're not on the homepage, navigate to homepage with FAQ hash
                                navigate(`${getLocalizedAppPath('')}#faq`);
                            }
                        }}
                    >
                        {t('pricing.faqLink', { defaultValue: 'Have questions? Check our FAQ' })}
                    </a>
                </div>
            </div>
        </div>
    );
}

export default PricingSection;
